/**
 * Enquête n°19 — Adapté de l'Étude de Cas CCS n°14
 * (Donner des notions sur la construction d'un accident, modèle de Reason.)
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const constructionAccidentEnquete = {
  slug: 'construction-accident',
  themeCode: 'A',
  issueNumber: 19,
  totalIssues: 19,
  series: 'enquetes',
  category: 'Enquête accidentologie & modélisation',
  shortTitle: 'Construction de l’accident',
  title: 'Le « fromage suisse » : pourquoi un accident, ce n’est jamais 1 cause',
  kicker: 'Enquête n°19 · Série « Témoignages & Sécurité »',
  subtitle:
    "Le modèle de James Reason (1990) explique pourquoi un accident n'est jamais la faute de quelqu'un, mais l'alignement de plusieurs trous dans plusieurs barrières. 4 catégories de causes, 3 phases (perception, décision, action). Comprendre, c'est pouvoir.",
  heroImage: '/img/magazines/premiers-secours.jpg',
  heroImageAlt: "Ambulance en intervention d'urgence — illustration de la construction d'un accident",
  readTimeMin: 9,
  seoDescription:
    "Enquête sur le modèle de Reason (fromage suisse) appliqué aux accidents 2RM : 4 catégories de causes, 3 phases perception/décision/action, défenses actives et passives.",
  seoKeywords:
    'modèle Reason, fromage suisse, construction accident, facteur humain, MAIDS, perception décision action',

  edito: {
    title: "Un accident, c'est rarement une cause — c'est presque toujours une chaîne",
    body: "« Il s'est tué parce qu'il roulait trop vite. » <strong>Faux.</strong> Il roulait trop vite, c'est vrai. Mais aussi : ses pneus avaient 7 ans, le revetement était gras après l'averse de 14h, son casque dataire de 2014 (EPS durci), il avait dormi 5h la nuit précédente, le panneau « virage dangereux » était caché par un arbre non taillé, le motard précédent ne lui avait pas signalé l'obstacle. <strong>Voilà le modèle du « fromage suisse » de James Reason (1990)</strong> : un accident n'est jamais la faute de quelqu'un — c'est l'alignement de plusieurs trous dans plusieurs barrières de sécurité. Cette enquête t'apprend à décomposer un accident comme un mécanicien décomposte une panne. Outil : l'étude de cas CCS n°14.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Le modèle de Reason en 60 secondes',
      paragraphs: [
        "<strong>Imagine une pile de tranches de gruyère emmental</strong>. Chaque tranche est une « barrière de sécurité » : ta vigilance, ton équipement, ta moto, l'infrastructure, les autres conducteurs. Chaque barrière a des <strong>trous</strong> (faiblesses). En temps normal, les trous d'une barrière sont compensés par les barrières suivantes — le danger ne passe pas.",
        "<strong>L'accident, c'est quand tous les trous s'alignent</strong>. Le danger traverse les barrières, atteint la victime. C'est un événement <strong>probabiliste</strong>, pas fatal. Renforcer une seule barrière suffit à empêcher l'alignement.",
        "<strong>Conséquences pratiques</strong>. 1) <em>Déculpabilisation</em> : tu n'es pas « le seul fautif » d'un accident. 2) <em>Responsabilisation</em> : tu peux renforcer chaque barrière individuellement. 3) <em>Prévention efficace</em> : agir sur n'importe quel maillon casse la chaîne.",
      ],
      keyPoints: [
        'Accident = alignement de plusieurs trous',
        'Renforcer 1 barrière suffit à éviter',
        'Approche probabiliste, pas fataliste',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "L'étude MAIDS (Motorcycle Accident In-Depth Study, 921 accidents européens analysés) attribue : <strong>60-80 % de cause humaine, 5-10 % de cause véhicule, 10-15 % d'environnement, 5-10 % d'infrastructure</strong>. Mais attention au piège : <em>« 70 % humain » ne veut pas dire « 70 % du motard »</em>. « Humain » inclut aussi le conducteur de l'autre véhicule (qui a refusé la priorité dans 50 % des accidents 2RM en ville), l'urbaniste qui a mal conçu le carrefour, le formateur qui a mal enseigné. Le « facteur humain » est un concept systémique, pas une accusation. C'est ce qui rend le modèle de Reason révolutionnaire : il <strong>déplace le regard de la culpabilité vers les barrières</strong>.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Les 4 catégories de causes et les 3 phases',
      paragraphs: [
        "<strong>Catégorie 1 — Causes humaines</strong> (60-80 %). Vitesse inadaptée, alcool, fatigue, distraction, in-expérience, surconfiance, refus de priorité par un autre. C'est la plus grande catégorie — mais aussi la plus modifiable individuellement.",
        "<strong>Catégorie 2 — Causes véhicule</strong> (5-10 %). Pneus usés, freins défaillants, éclairage HS, jeux excessifs dans la direction. Prévention : <strong>contrôle visuel avant chaque trajet</strong> (3 min) + entretien programmé (toutes les 5-10 000 km).",
        "<strong>Catégorie 3 — Environnement</strong> (10-15 %). Pluie, brouillard, soleil rasant, périle gras (gazole, gravillons, feuilles), animaux. Difficile à éviter, possible à anticiper (météo avant départ, ralentissement en zone risque).",
        "<strong>Catégorie 4 — Infrastructure</strong> (5-10 %). Signalisation mal posée ou cachée, marquage effacé, nids de poule, glissières manquantes. Du ressort de la collectivité, mais on peut <strong>signaler en mairie</strong>.",
        "<strong>Les 3 phases de l'accident</strong>. <em>1. Perception</em> (le danger arrive) : vision, audition, attention. <em>2. Décision</em> (que faire ?) : analyse cognitive, choix de l'action. <em>3. Action</em> (j'exécute) : freinage, esquive, klaxon. À chaque phase, tu peux échouer (perception tardive, décision erronée, action mal coordonnée).",
      ],
      keyPoints: [
        '4 catégories : humain / véhicule / environnement / infrastructure',
        '3 phases : perception → décision → action',
        'Chaque phase = barrière potentielle',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Je n'y étais pour rien, c'est l'autre qui m'a coupé la route. » <strong>Légalement vrai parfois, systémiquement faux toujours.</strong> Le modèle de Reason demande : <em>était-il prévisible que l'autre te coupe la route ?</em>. Et la réponse est presque toujours OUI — sortie de stationnement non-répertoriée, intersection à visibilité réduite, route fréquentée par des SUV avec angles morts énormes. <strong>Chaîne complète</strong> : tu connaissais le risque → tu n'as pas ralenti → tu ne portais pas de gilet HV → ton casque était noir → tes feux étaient affaiblis. <strong>Tu ne portes pas la faute, mais tu peux porter la prévention</strong>. C'est tout l'enjeu de la responsabilisation systemique : ne pas chercher à qui la faute, mais à quoi la barrière renforce.",
    },
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Pour l'étude de cas n°14, je projette toujours <strong>un accident réel filmé</strong> (avec autorisation). Le groupe (12 motards expérimentés) doit identifier toutes les barrières qui auraient pu empêcher l'accident. Au début, ils trouvent 3-4 (« il roulait trop vite, l'autre l'a coupé »). Après 40 min de décomposition Reason : 18-22 barrières identifiées. <strong>C'est le moment du déclic.</strong> Mon conseil pour toi, motard ou cycliste : <strong>avant chaque trajet, demande-toi quelle barrière tu vas renforcer aujourd'hui</strong>. Pression pneus ? Concentration sans téléphone ? Anticipation des angles morts ? Une barrière renforcée par jour, c'est 365 trous combés par an. »",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '>95 %',
    middle: 'des accidents',
    label: "sont évitables selon le modèle de Reason. La fatalité absolue n'existe presque pas — elle est l'alignement de plusieurs erreurs réparables individuellement.",
    source: 'Modèle de Reason (1990) + étude MAIDS 2009',
    accent: '#6b7280',
  },

  glossary: [
    { term: 'Modèle de Reason', def: 'Théorie de James Reason (1990) : un accident est l\'alignement de plusieurs trous dans plusieurs barrières de sécurité. Aussi appelé « modèle du fromage suisse ».' },
    { term: 'Barrière de sécurité', def: 'Mécanisme de prévention ou de protection. Peut être humaine (vigilance), technique (ABS), structurelle (glissière), procédurale (formation).' },
    { term: 'Défense active', def: 'Barrière nécessitant une action consciente (port du casque, ralentissement). À distinguer des défenses passives.' },
    { term: 'Défense passive', def: 'Barrière fonctionnant sans intervention humaine (ABS, glissière routière, airbag). Active automatiquement.' },
    { term: 'Perception-Décision-Action', def: 'Les 3 phases du processus accidentel. Échec possible à chaque phase. Chaque phase = barrière potentielle.' },
    { term: 'Facteur humain', def: 'Catégorie causale dominante (60-80 % MAIDS). Inclut toutes les actions/inactions humaines : conducteur, autre usager, formateur, urbaniste.' },
  ],

  ecsrCorner: {
    title: 'Côté formateur ECSR',
    intro:
      "L'étude de cas n°14 est une intervention « notions » (transmission sans évaluation finale) en association de motocyclistes. Public adulte expérimenté.",
    points: [
      'Public-cible : 12 motards adhérents d\'une association locale (adultes expérimentés).',
      'Type d\'intervention : donner des notions (typologie spécifique ECSR : transmission sans restitution attendue).',
      'Durée : 1h30 — apports théoriques 45 min + analyse cas réel en groupe 30 min + débat 15 min.',
      'Méthode : expositif Reason + schémas « fromage suisse » + analyse d\'un accident réel filmé par décomposition des barrières.',
      "Résultat attendu : le motard comprend le modèle Reason, identifie les 4 catégories de causes, décrit les 3 phases, distingue défenses actives/passives.",
    ],
    seeCasLink: {
      label: "Voir l'étude de cas CCS n°14 complète (jury ECSR)",
      to: '/enseignant/etudes-de-cas',
      testid: 'see-cas-ccs-14',
    },
  },

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur le modèle de Reason, les 4 catégories de causes, les 3 phases, défenses actives/passives. Réussite minimum : 8/10.",
    themeCode: 'A',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: null,
};

export default constructionAccidentEnquete;
